import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { useStore } from "@xyflow/react";

import { Icon } from "@/components";

import {
	describeStepPath,
	PathEdge,
	PathNode,
	resolveStepPathWarning,
	STEP_PATH_WARNING_KEYS,
} from "../../utils/campaignStepPath.util";

/** The amber line a step node shows under its header when something keeps it from working. */
export const StepWarningLine: FunctionComponent<{ text: string; testId: string }> = ({
	text,
	testId,
}) => (
	<div
		className="flex items-start gap-1.5 px-2 py-1.5 text-xs text-status-warning bg-status-warning-light"
		data-testid={testId}
	>
		<Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" icon={faTriangleExclamation} />
		<span>{text}</span>
	</div>
);

interface StepPathWarningNoteProps {
	nodeId: string;
	/** The step's own type — a node can be switched to another channel after it was placed. */
	stepType: string;
}

/**
 * Says when a step's place in the sequence keeps it from reaching the leads that get there
 * (PREC-3897). It reads the graph from the flow's own store, so the Campaign Builder and the
 * per-lead copy on the Messages pages show the same warning without either wiring it in. The
 * selector returns a string, so a node re-renders only when its own warning changes. A warning,
 * never a block: the campaign still saves, and an existing campaign is not rewritten.
 */
export const StepPathWarningNote: FunctionComponent<StepPathWarningNoteProps> = ({
	nodeId,
	stepType,
}) => {
	const { t } = useTranslation();
	const warning = useStore((state) =>
		resolveStepPathWarning(
			stepType,
			describeStepPath(nodeId, state.nodes as PathNode[], state.edges as PathEdge[])
		)
	);

	return warning ? (
		<StepWarningLine testId="step-path-warning" text={t(STEP_PATH_WARNING_KEYS[warning])} />
	) : null;
};

/** The InMail step's caveat when none of the campaign's LinkedIn senders can send one. */
export const InmailSenderWarning: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<StepWarningLine
			testId="inmail-sender-warning"
			text={t("list.campaignBuilder.stepPath.inmailNeedsSender")}
		/>
	);
};
